// „Co powiesz?” (SPEC §23.13): ocena wypowiedzi dziecka. Wymowę ocenia Azure (zdanie odniesienia = poprawna odpowiedź), a TEN moduł rozstrzyga, CO dziecko
// powiedziało: poprawne zdanie (także krzywo wymówione albo z drobnym dodatkiem), inne zdanie z karty (np. „Is that all?” zamiast „Can I have a banana?”)
// czy nic sensownego. Porównuje tekst rozpoznany (`recognizedText` z oceny Azure) ze zdaniami z karty na poziomie słów. Czysty kod bez wejścia/wyjścia.

import { expandContraction, tokenize } from './lemma.ts';

const FILLERS = new Set(['um', 'uh', 'hmm', 'mm', 'er', 'erm']);
const DIGITS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty'];

/** Słowa zdania do porównania: małe litery, bez interpunkcji, skróty rozwinięte (I'm = I am), a/an to samo, cyfry słowami, bez „yyy”. */
export function sayWords(text: string): string[] {
  const spelled = text.replace(/\b(\d{1,2})\b/g, (m) => DIGITS[Number(m)] ?? m);
  const out: string[] = [];
  for (const t of tokenize(spelled)) {
    for (const p of expandContraction(t.lower) ?? [t.lower]) {
      if (FILLERS.has(p)) continue;
      out.push(p === 'an' ? 'a' : p);
    }
  }
  return out;
}

/** Odległość Levenshteina na słowach: ile słów trzeba wstawić, usunąć lub zamienić, żeby z `hyp` zrobić `ref`. */
export function wordErrors(ref: readonly string[], hyp: readonly string[]): number {
  let prev = Array.from({ length: hyp.length + 1 }, (_, j) => j);
  for (let i = 1; i <= ref.length; i++) {
    const cur = [i];
    for (let j = 1; j <= hyp.length; j++) cur[j] = Math.min(prev[j]! + 1, cur[j - 1]! + 1, prev[j - 1]! + (ref[i - 1] === hyp[j - 1] ? 0 : 1));
    prev = cur;
  }
  return prev[hyp.length]!;
}

/** Ile błędów w słowach wolno zrobić przy zdaniu o `n` słowach: do 2 słów — żadnego, 3–5 — jeden, 6–8 — dwa, dalej ok. 1/3 długości. */
export function allowedErrors(n: number): number {
  return n <= 2 ? 0 : Math.floor(n * 0.34);
}

/** Czy dwa zdania są na tyle różne, że rozpoznawanie mowy ich nie pomyli (obszary dopuszczalnych błędów obu zdań się nie stykają)? */
export function distinguishable(a: string, b: string): boolean {
  const wa = sayWords(a);
  const wb = sayWords(b);
  return wordErrors(wa, wb) > allowedErrors(wa.length) + allowedErrors(wb.length);
}

export type SayVerdict =
  | { kind: 'correct'; errors: number; matched: string }
  | { kind: 'distractor'; index: number; errors: number }
  | { kind: 'unclear'; reason: 'empty' | 'nomatch' };

/**
 * Co dziecko powiedziało? `accept` = poprawna odpowiedź i jej zaakceptowane warianty, `distractors` = błędne opcje z karty.
 * Wygrywa kandydat z najmniejszym udziałem błędów, o ile mieści się w dopuszczalnej liczbie błędów; remis rozstrzyga na korzyść poprawnej odpowiedzi.
 */
export function judgeSpoken(recognized: string, accept: readonly string[], distractors: readonly string[]): SayVerdict {
  const hyp = sayWords(recognized);
  if (hyp.length === 0) return { kind: 'unclear', reason: 'empty' };
  const score = (text: string): { errors: number; share: number; ok: boolean } => {
    const ref = sayWords(text);
    const errors = wordErrors(ref, hyp);
    return { errors, share: errors / Math.max(1, ref.length), ok: errors <= allowedErrors(ref.length) };
  };
  let best: { kind: 'correct'; matched: string; errors: number; share: number } | undefined;
  for (const text of accept) {
    const s = score(text);
    if (s.ok && (!best || s.share < best.share)) best = { kind: 'correct', matched: text, errors: s.errors, share: s.share };
  }
  let wrong: { index: number; errors: number; share: number } | undefined;
  distractors.forEach((text, index) => {
    const s = score(text);
    if (s.ok && (!wrong || s.share < wrong.share)) wrong = { index, errors: s.errors, share: s.share };
  });
  if (best && (!wrong || best.share <= wrong.share)) return { kind: 'correct', errors: best.errors, matched: best.matched };
  if (wrong) return { kind: 'distractor', index: wrong.index, errors: wrong.errors };
  return { kind: 'unclear', reason: 'nomatch' };
}
